/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useEffect, useMemo } from 'react';
import { useUserPayouts } from '../hooks/usePayouts.ts';
import { formatCurrency, formatDateOnly } from '../utils/formatters.ts';
import { useCollection } from '../contexts/CollectionContext.tsx';
import SalesDashboardSection from './SalesDashboardSection.tsx';
import './UserPayouts.css';

const UserPayouts: React.FC = () => {
  // USE GLOBAL CONTEXT
  const { 
    userCollections: userHandles, // Now an array of handles
    selectedCollectionIndex,
    getCollectionTitle // Function to get title from handle
  } = useCollection();

  // Get user's payout history using collection handles from context
  const { 
    payouts, 
    totalAmount, 
    summaryByCollection,
    loading: payoutsLoading,
    error: payoutsError
  } = useUserPayouts(userHandles);

  // Filter payouts by selected collection handle (from global context)
  const selectedHandle = userHandles[selectedCollectionIndex];
  const filteredPayouts = selectedHandle
    ? payouts.filter(payout => {
        // Match on collectionHandle if it exists, otherwise fall back to title matching
        if (payout.collectionHandle) {
          return payout.collectionHandle === selectedHandle;
        }
        // Fallback: match title with the handle's title
        const expectedTitle = getCollectionTitle(selectedHandle);
        return payout.collectionTitle === expectedTitle;
      })
    : payouts;

  const filteredTotalAmount = filteredPayouts.reduce((sum, payout) => sum + payout.amount, 0);

  // Get display name for selected collection
  const selectedCollectionName = selectedHandle ? getCollectionTitle(selectedHandle) : null;

  // The highest order number covered by any of the selected collection's
  // payouts, or null if it's never had one - narrows how far back the
  // earned-since-payment figure's order-history fetch needs to look.
  // Order-number based rather than date based - see useEarnedSincePayout
  // for why. Computed independently of filteredPayouts above (which isn't
  // memoized) so this stays a stable reference across renders unless the
  // underlying data changes.
  const lastOrderNumberPaid = useMemo(() => {
    if (!selectedHandle) return null;

    const matching = payouts.filter((payout) => {
      if (payout.collectionHandle) {
        return payout.collectionHandle === selectedHandle;
      }
      const expectedTitle = getCollectionTitle(selectedHandle);
      return payout.collectionTitle === expectedTitle;
    });

    if (matching.length === 0) return null;

    const numbers = matching
      .map((payout) => payout.lastOrderNumberPaid)
      .filter((n): n is number => typeof n === 'number' && !isNaN(n));

    if (numbers.length === 0) return null;

    return Math.max(...numbers);
  }, [payouts, selectedHandle, getCollectionTitle]);

  // Debug logging
  useEffect(() => {
    console.log('💰 Payouts loading:', payoutsLoading);
    console.log('💰 Payouts data:', payouts);
    console.log('💰 Payouts count:', payouts.length);
    console.log('💰 Total amount:', totalAmount);
    console.log('💰 Error:', payoutsError);
    console.log('💰 Selected collection index:', selectedCollectionIndex);
    console.log('💰 Selected handle:', selectedHandle);
    console.log('💰 Selected collection name:', selectedCollectionName);
  }, [payouts, payoutsLoading, totalAmount, payoutsError, selectedCollectionIndex, selectedHandle, selectedCollectionName]);

  return (
    <div className="user-payouts-container">
      <div className="user-payouts-header">
        {/* Header content if needed */}
      </div>

      <SalesDashboardSection
        selectedCollectionName={selectedCollectionName}
        selectedHandle={selectedHandle}
        lastOrderNumberPaid={lastOrderNumberPaid}
        payouts={payouts}
        payoutsLoading={payoutsLoading}
      />

      {payoutsLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading payment history...</p>
        </div>
      ) : filteredPayouts.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-icon">💰</div>
          <h2>No Payments Yet</h2>
          <p>Your payment history will appear here once payments are processed.</p>
          {selectedCollectionName && payouts.length > 0 && (
            <p style={{ fontSize: '14px', color: '#666', marginTop: '20px' }}>
              No payments for "{selectedCollectionName}".
            </p>
          )}
        </div>
      ) : (
         <div className="cards-container">
          {/* Summary Card */}
            {/* Breakdown by Collection - Only show when viewing all collections */}
            {!selectedHandle && summaryByCollection.size > 1 && (
                <div className="payout-summary-card">
                <div className="collection-breakdown">
                    <h3>Breakdown by Collection</h3>
                    <div className="collection-grid">
                    {Array.from(summaryByCollection.entries()).map(([collection, data]) => (
                        <div key={collection} className="collection-item">
                        <div className="collection-info">
                            <span className="collection-name">{collection}</span>
                            <span className="collection-count">{data.count} payment{data.count !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="collection-amount">{formatCurrency(data.total)}</div>
                        </div>
                    ))}
                    </div>
                </div>
               </div>
            )}

          {/* Payment List */}
          <div className="payouts-list-card">
            <div className="payouts-details-header">
               <h2>Payment Details</h2>
               <div className="summary-main">
                    <div className="summary-label">
                        Total Paid ({filteredPayouts.length}):
                    </div>
                    <div className="summary-amount">{formatCurrency(filteredTotalAmount)}</div>
                </div>
            </div>

            <div className="payouts-list">
              {filteredPayouts.map((payout) => (
                <div key={payout.id} className="payout-item">
                  <div className="payout-header">
                    <div className="payout-amount-section">
                      <span className="payout-amount">{formatCurrency(payout.amount)}</span>
                      <span className="payout-status">✓ Paid</span>
                    </div>
                    <span className="payout-date">{formatDateOnly(payout.date)}</span>
                  </div>

                  <div className="payout-body">
                    {!selectedHandle && (
                      <div className="payout-detail">
                        <span className="detail-label">Collection</span>
                        <span className="detail-value">{payout.collectionTitle}</span>
                      </div>
                    )}

                    <div className="payout-detail">
                      <span className="detail-label">Payment Method:</span>
                      <span className="detail-value">{payout.method}</span>
                    </div>

                    {payout.confirmationNumber && (
                      <div className="payout-detail">
                        <span className="detail-label">Confirmation #</span>
                        <span className="detail-value confirmation">{payout.confirmationNumber}</span>
                      </div>
                    )}

                    {payout.orders && payout.orders.length > 0 && (
                      <details className="payout-detail orders orders-accordion">
                        <summary className="detail-label orders-accordion-toggle">
                          Orders Included ({payout.orders.length})
                        </summary>
                        <div className="detail-value orders-accordion-panel">
                          <div className="orders-list">
                            {payout.orders.map((order, idx) => (
                              <span key={idx} className="order-badge">#{order.number}</span>
                            ))}
                          </div>
                        </div>
                      </details>
                    )}

                    {payout.notes && (
                      <div className="payout-detail notes">
                        <span className="detail-label">Notes:</span>
                        <span className="detail-value">{payout.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
      )}
    </div>
  );
};

export default UserPayouts;